import { PayloadAction } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import { NotifyService } from 'helpers';
import { PayloadActionData, RESPONSE_TYPE, ResponseType } from 'models';
import {
  BASE_REQUEST_TYPE,
  PURCHASE_HISTORY_PARAMS_REQUEST,
  PURCHASE_HISTORY_STATE,
  SITTER_AC_PARAMS_REQUEST,
  SITTER_AC_STATE,
  SUBSCRIPTION_DETAIL_TYPE,
  SUBSCRIPTION_PARAMS_REQUEST,
  SUBSCRIPTION_STATE,
} from 'models/subscription';
import { call, put, takeLatest } from 'redux-saga/effects';
import {
  deleteSitterACApi,
  getBaseRequestDetailApi,
  getPurchaseHistoryApi,
  getSitterACListApi,
  getSubscriptionDetailApi,
  getSubscriptionListApi,
} from 'services/subscription';
import { setGlobalLoading, setLoadingPopup } from 'store/reducers/global';
import {
  setBaseRequestDetailSuccess,
  setPurchaseHistorySuccess,
  setSitterAddedIntoACJobListSuccess,
  setSubscriptionDetailSuccess,
  setSubscriptionListSuccess,
} from 'store/reducers/subscription';
import {
  DELETE_SITTER_AC_REQUEST,
  GET_AC_PURCHASE_HISTORY_LIST_REQUEST,
  GET_BASE_REQUEST_DETAIL_REQUEST,
  GET_SITTER_AC_LIST_REQUEST,
  GET_SUBSCRIPTION_DETAIL_REQUEST,
  GET_SUBSCRIPTION_LIST_REQUEST,
} from 'store/reducers/subscription/actionType';

function* getSubscriptionRequest(
  action: PayloadAction<{
    params?: SUBSCRIPTION_PARAMS_REQUEST;
  }>
) {
  try {
    yield put(setGlobalLoading(true));
    const response: AxiosResponse<{ data: SUBSCRIPTION_STATE }> = yield call(
      getSubscriptionListApi,
      action.payload.params
    );
    yield put(
      setSubscriptionListSuccess({
        records: response.data.data.records,
        metadata: response.data.data.metadata,
      })
    );
    yield put(setGlobalLoading(false));
  } catch (error) {
    NotifyService.error(error);
    yield put(setGlobalLoading(false));
  }
}

function* getSubscriptionDetailRequest(
  action: PayloadAction<PayloadActionData<{ id: string }>>
) {
  if (action.payload.isPopup) yield put(setLoadingPopup(true));
  else yield put(setGlobalLoading(true));
  try {
    const response: AxiosResponse<ResponseType<SUBSCRIPTION_DETAIL_TYPE>> =
      yield call(getSubscriptionDetailApi, action.payload.data.id);
    yield put(setSubscriptionDetailSuccess(response.data.data));
    if (action.payload.onSuccess) action.payload.onSuccess();
  } catch (error: unknown) {}
  if (action.payload.isPopup) yield put(setLoadingPopup(false));
  else yield put(setGlobalLoading(false));
}

function* getBaseRequestDetailRequest(
  action: PayloadAction<PayloadActionData<{ id: string }>>
) {
  if (action.payload.isPopup) yield put(setLoadingPopup(true));
  else yield put(setGlobalLoading(true));
  try {
    const response: AxiosResponse<ResponseType<BASE_REQUEST_TYPE>> = yield call(
      getBaseRequestDetailApi,
      action.payload.data.id
    );
    yield put(setBaseRequestDetailSuccess(response.data.data));
    if (action.payload.onSuccess) action.payload.onSuccess();
  } catch (error: unknown) {}
  if (action.payload.isPopup) yield put(setLoadingPopup(false));
  else yield put(setGlobalLoading(false));
}

function* getSitterACRequest(
  action: PayloadAction<{
    params?: SITTER_AC_PARAMS_REQUEST;
  }>
) {
  try {
    yield put(setLoadingPopup(true));
    const response: AxiosResponse<SITTER_AC_STATE> = yield call(
      getSitterACListApi,
      action.payload.params
    );
    yield put(
      setSitterAddedIntoACJobListSuccess({
        data: response.data.data,
        metadata: response.data.metadata,
      })
    );
    yield put(setLoadingPopup(false));
  } catch (error) {
    NotifyService.error(error);
    yield put(setLoadingPopup(false));
  }
}

function* deleteSitterACRequest(
  action: PayloadAction<{
    id: string;
    onSuccess?: () => void;
  }>
) {
  try {
    yield put(setGlobalLoading(true));
    yield call(deleteSitterACApi, action.payload.id);
    if (action.payload.onSuccess) action.payload.onSuccess();
  } catch (error: unknown) {
    yield put(setGlobalLoading(false));
    NotifyService.error(error);
  } finally {
    yield put(setGlobalLoading(false));
  }
}

function* getPurchaseHistoryListRequest(
  action: PayloadAction<{
    params?: PURCHASE_HISTORY_PARAMS_REQUEST;
  }>
) {
  try {
    yield put(setLoadingPopup(true));
    const response: AxiosResponse<RESPONSE_TYPE<PURCHASE_HISTORY_STATE>> =
      yield call(getPurchaseHistoryApi, action.payload.params);
    yield put(
      setPurchaseHistorySuccess({
        records: response.data.data.records,
        metadata: response.data.data.metadata,
      })
    );
    yield put(setLoadingPopup(false));
  } catch (error) {
    NotifyService.error(error);
    yield put(setLoadingPopup(false));
  }
}

function* subscription() {
  yield takeLatest(GET_SUBSCRIPTION_LIST_REQUEST, getSubscriptionRequest);
  yield takeLatest(
    GET_SUBSCRIPTION_DETAIL_REQUEST,
    getSubscriptionDetailRequest
  );
  yield takeLatest(
    GET_BASE_REQUEST_DETAIL_REQUEST,
    getBaseRequestDetailRequest
  );
  yield takeLatest(GET_SITTER_AC_LIST_REQUEST, getSitterACRequest);
  yield takeLatest(DELETE_SITTER_AC_REQUEST, deleteSitterACRequest);
  yield takeLatest(
    GET_AC_PURCHASE_HISTORY_LIST_REQUEST,
    getPurchaseHistoryListRequest
  );
}

export default subscription;
